import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X, ClipboardPaste, Link2, Upload, RefreshCw, Loader2, AlertCircle, Save, Check, Trash2, Sparkles, Database, MessageSquare, ChevronDown } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import IngestionEventTimeline from "./IngestionEventTimeline";
import type { IngestionDraft, SourceType, IngestionStatus, IngestionDataType, RefreshHint, ReviewStatus } from "./types";

interface IngestionDraftPreviewProps {
  draft: IngestionDraft;
  onClear: () => void;
  onUpdate: (updates: Partial<IngestionDraft>) => void;
  onRescrape?: () => void;
  onSave?: () => void;
  onDelete?: () => void;
  onExtract?: () => void;
  isSaving?: boolean;
  isDeleting?: boolean;
  isExtracting?: boolean;
  savedIngestionId?: string | null;
  reviewStatus?: ReviewStatus | null;
  onReviewStatusChange?: (status: ReviewStatus | null) => void;
  structuredExtraction?: Record<string, unknown> | null;
  aiNotes?: Record<string, unknown> | null;
}

const sourceIcons: Record<SourceType, React.ReactNode> = {
  paste: <ClipboardPaste className="w-3 h-3" />,
  link: <Link2 className="w-3 h-3" />,
  upload: <Upload className="w-3 h-3" />,
};

const sourceLabels: Record<SourceType, string> = {
  paste: "Pasted Text",
  link: "URL",
  upload: "File Upload",
};

const statusLabels: Record<IngestionStatus, string> = {
  captured: "Captured",
  pending: "Pending",
  processing: "Processing",
  review: "Awaiting Review",
  processed: "Processed",
  failed: "Failed",
};

const statusColors: Record<IngestionStatus, string> = {
  captured: "bg-blue-500/20 text-blue-300 border-blue-400/30",
  pending: "bg-yellow-500/20 text-yellow-300 border-yellow-400/30",
  processing: "bg-orange-500/20 text-orange-300 border-orange-400/30",
  review: "bg-purple-500/20 text-purple-300 border-purple-400/30",
  processed: "bg-green-500/20 text-green-300 border-green-400/30",
  failed: "bg-red-500/20 text-red-300 border-red-400/30",
};

const dataTypeLabels: Record<IngestionDataType, string> = {
  provider: "Provider",
  program: "Program",
  schedule: "Schedule",
  calendar: "Calendar",
  category: "Category-level",
  other: "Other",
};

const refreshHintLabels: Record<RefreshHint, string> = {
  static: "Static",
  periodic: "Periodic",
  manual: "Manual",
  unknown: "Unknown",
};

const reviewStatusLabels: Record<ReviewStatus, string> = {
  pending: "Pending Review",
  approved: "Approved",
  rejected: "Rejected",
};

const reviewStatusColors: Record<ReviewStatus, string> = {
  pending: "bg-yellow-500/20 text-yellow-300 border-yellow-400/30",
  approved: "bg-green-500/20 text-green-300 border-green-400/30",
  rejected: "bg-red-500/20 text-red-300 border-red-400/30",
};

const IngestionDraftPreview = ({
  draft,
  onClear,
  onUpdate,
  onRescrape,
  onSave,
  onDelete,
  onExtract,
  isSaving,
  isDeleting,
  isExtracting,
  savedIngestionId,
  reviewStatus,
  onReviewStatusChange,
  structuredExtraction,
  aiNotes,
}: IngestionDraftPreviewProps) => {
  const [extractionOpen, setExtractionOpen] = React.useState(true);
  const { source, raw_content, status, data_type, scraped_at, scrape_error } = draft;
  const timestamp = new Date(source.timestamp).toLocaleString();
  const isProcessing = status === "processing" || isExtracting;
  const isLinkSource = source.source_type === "link";
  const isSaved = !!savedIngestionId;

  return (
    <div className="flex flex-col gap-3 mt-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-white border-white/30 gap-1">
            {sourceIcons[source.source_type]}
            {sourceLabels[source.source_type]}
          </Badge>
          <Badge variant="outline" className={`gap-1 ${statusColors[status]}`}>
            {isProcessing && <Loader2 className="w-3 h-3 animate-spin" />}
            {statusLabels[status]}
          </Badge>
          {isSaved && (
            <Badge variant="outline" className="text-green-300 border-green-400/30 bg-green-500/20 gap-1">
              <Check className="w-2.5 h-2.5" />
              Saved
            </Badge>
          )}
          {isLinkSource && source.refresh_hint && (
            <Badge variant="outline" className="text-white/60 border-white/20 gap-1 text-xs">
              <RefreshCw className="w-2.5 h-2.5" />
              {refreshHintLabels[source.refresh_hint]}
            </Badge>
          )}
          <span className="text-white/50 text-xs">{timestamp}</span>
        </div>
        <div className="flex items-center gap-1">
          {isSaved && onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              disabled={isDeleting || isProcessing}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-6 w-6 p-0"
            >
              {isDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="text-white/60 hover:text-white hover:bg-white/10 h-6 w-6 p-0"
            disabled={isProcessing}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Scraped At & Re-scrape Button */}
      {isLinkSource && (
        <div className="flex items-center gap-3 flex-wrap">
          {scraped_at && (
            <span className="text-white/50 text-xs">
              Scraped: {new Date(scraped_at).toLocaleString()}
            </span>
          )}
          {onRescrape && !isProcessing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRescrape}
              className="text-white/60 hover:text-white hover:bg-white/10 h-6 gap-1 text-xs"
            >
              <RefreshCw className="w-3 h-3" />
              Re-scrape
            </Button>
          )}
        </div>
      )}

      {/* Scrape Error */}
      {scrape_error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-400/30 rounded-md px-3 py-2">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
          <span className="text-red-300 text-sm flex-1">{scrape_error}</span>
          {onRescrape && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRescrape}
              className="text-red-300 hover:text-red-200 hover:bg-red-500/20 h-6 text-xs"
            >
              Retry
            </Button>
          )}
        </div>
      )}

      {/* Editable Fields */}
      <div className="flex gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-white/50 text-xs">Status:</span>
          <Select 
            value={status} 
            onValueChange={(v) => onUpdate({ status: v as IngestionStatus })}
            disabled={isProcessing}
          >
            <SelectTrigger className="bg-white/10 border-white/20 text-white h-7 text-xs w-auto min-w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-white/20">
              {(Object.keys(statusLabels) as IngestionStatus[]).map((s) => (
                <SelectItem
                  key={s}
                  value={s}
                  className="text-white/80 focus:bg-white/10 focus:text-white text-xs"
                >
                  {statusLabels[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-white/50 text-xs">Data Type:</span>
          <Select
            value={data_type ?? "unset"}
            onValueChange={(v) => onUpdate({ data_type: v === "unset" ? null : (v as IngestionDataType) })}
            disabled={isProcessing}
          >
            <SelectTrigger className="bg-white/10 border-white/20 text-white h-7 text-xs w-auto min-w-[130px]">
              <SelectValue placeholder="Not set" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-white/20">
              <SelectItem
                value="unset"
                className="text-white/50 focus:bg-white/10 focus:text-white text-xs italic"
              >
                Not set
              </SelectItem>
              {(Object.keys(dataTypeLabels) as IngestionDataType[]).map((dt) => (
                <SelectItem
                  key={dt}
                  value={dt}
                  className="text-white/80 focus:bg-white/10 focus:text-white text-xs"
                >
                  {dataTypeLabels[dt]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Review Status - only show for saved ingestions */}
        {isSaved && onReviewStatusChange && (
          <div className="flex items-center gap-2">
            <span className="text-white/50 text-xs">Review:</span>
            <Select
              value={reviewStatus ?? "unset"}
              onValueChange={(v) => onReviewStatusChange(v === "unset" ? null : (v as ReviewStatus))}
              disabled={isProcessing}
            >
              <SelectTrigger className={`h-7 text-xs w-auto min-w-[130px] ${
                reviewStatus ? reviewStatusColors[reviewStatus] : "bg-white/10 border-white/20 text-white"
              }`}>
                <SelectValue placeholder="Not reviewed" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-white/20">
                <SelectItem
                  value="unset"
                  className="text-white/50 focus:bg-white/10 focus:text-white text-xs italic"
                >
                  Not reviewed
                </SelectItem>
                {(Object.keys(reviewStatusLabels) as ReviewStatus[]).map((rs) => (
                  <SelectItem
                    key={rs}
                    value={rs}
                    className="text-white/80 focus:bg-white/10 focus:text-white text-xs"
                  >
                    {reviewStatusLabels[rs]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Save Button - only show for unsaved drafts */}
      {!isSaved && onSave && raw_content && (
        <Button
          onClick={onSave}
          disabled={isSaving || isProcessing || !raw_content}
          className="w-full bg-white/20 hover:bg-white/30 text-white border-white/30"
          variant="outline"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Ingestion
            </>
          )}
        </Button>
      )}

      {/* Extract Button - only show for saved drafts with data_type set */}
      {isSaved && onExtract && data_type && (
        <Button
          onClick={onExtract}
          disabled={isExtracting || !raw_content}
          className="w-full bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 border-purple-400/30"
          variant="outline"
        >
          {isExtracting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Extracting...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Extract with AI
            </>
          )}
        </Button>
      )}

      {/* Raw Content Preview */}
      <div className="bg-white/5 rounded-md p-3 border border-white/10 relative">
        {isExtracting && (
          <div className="absolute inset-0 bg-black/40 rounded-md flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}
        <p className="text-white/70 text-xs uppercase tracking-wide mb-1">Captured Content</p>
        <ScrollArea className="max-h-40">
          <p className="text-white text-sm whitespace-pre-wrap">
            {raw_content || (status === "processing" ? "Scraping content..." : "No content yet")}
          </p>
        </ScrollArea>
      </div>

      {/* Structured Extraction */}
      <div className="bg-white/5 rounded-md p-3 border border-white/10">
        <Collapsible open={extractionOpen} onOpenChange={setExtractionOpen}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="w-3 h-3 text-purple-400" />
              <p className="text-purple-300 text-xs uppercase tracking-wide">
                Structured Extraction
              </p>
            </div>
            <CollapsibleTrigger className="hover:bg-white/10 p-1 rounded">
              <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${extractionOpen ? "" : "-rotate-90"}`} />
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent>
            {structuredExtraction && Object.keys(structuredExtraction).length > 0 ? (
              <ScrollArea className="max-h-60 mt-2">
                <pre className="text-xs text-white/80 overflow-x-auto font-mono bg-black/20 p-2 rounded">
                  {JSON.stringify(structuredExtraction, null, 2)}
                </pre>
              </ScrollArea>
            ) : (
              <p className="text-white/40 text-sm italic mt-2">
                {isSaved && data_type 
                  ? 'No extraction yet. Click "Extract with AI" to process.'
                  : isSaved
                  ? "Set a data type first, then extract."
                  : "Save ingestion first, then extract."}
              </p>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* AI Notes */}
      <div className="bg-white/5 rounded-md p-3 border border-white/10">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="w-3 h-3 text-blue-400" />
          <p className="text-blue-300 text-xs uppercase tracking-wide">AI Notes</p>
        </div>
        {aiNotes && Object.keys(aiNotes).length > 0 ? (
          <div className="space-y-1.5">
            {aiNotes.summary && (
              <p className="text-white/80 text-sm">{String(aiNotes.summary)}</p>
            )}
            {aiNotes.confidence && (
              <Badge 
                variant="outline" 
                className={`text-xs ${
                  aiNotes.confidence === "high" ? "text-green-300 border-green-400/30" :
                  aiNotes.confidence === "medium" ? "text-yellow-300 border-yellow-400/30" :
                  "text-red-300 border-red-400/30"
                }`}
              >
                Confidence: {String(aiNotes.confidence)}
              </Badge>
            )}
            {Array.isArray(aiNotes.missing_fields) && aiNotes.missing_fields.length > 0 && (
              <p className="text-white/50 text-xs">
                Missing: {aiNotes.missing_fields.join(", ")}
              </p>
            )}
            {Array.isArray(aiNotes.ambiguities) && aiNotes.ambiguities.length > 0 && (
              <p className="text-white/50 text-xs">
                Ambiguities: {aiNotes.ambiguities.join(", ")}
              </p>
            )}
          </div>
        ) : (
          <p className="text-white/40 text-sm italic">
            AI observations will appear after extraction.
          </p>
        )}
      </div>

      {/* Event Timeline - only show for saved ingestions */}
      {isSaved && <IngestionEventTimeline ingestionId={savedIngestionId} />}
    </div>
  );
};

export default IngestionDraftPreview;
